import type { ColumnDef, ColumnOrderState } from '@tanstack/react-table';

/** Empty gutter after the last data column so edge dropdowns can scroll into view (DL-60609). */
export const TABLE_EDGE_SCROLL_GUTTER_WIDTH = 150;

export const FILLER_COLUMN_ID = 'Filler';
export const SELECTOR_COLUMN_ID = 'Selector';

const isCustomFieldColumnId = (id: string): boolean => id.startsWith('cf_');

const getDataColumnIds = (allLeafColumnIds: string[]): string[] =>
  allLeafColumnIds.filter(id => id !== SELECTOR_COLUMN_ID && id !== FILLER_COLUMN_ID);

/** Keep the scroll-gutter column last regardless of saved column order. */
export const withFillerColumnLast = (order: string[]): string[] => {
  const without = order.filter(id => id !== FILLER_COLUMN_ID);
  return [...without, FILLER_COLUMN_ID];
};

const insertAtDefinitionPosition = (
  result: string[],
  id: string,
  definitionIds: string[]
): string[] => {
  const defIndex = definitionIds.indexOf(id);
  if (defIndex === -1) return [...result, id];

  for (let i = defIndex - 1; i >= 0; i -= 1) {
    const prev = definitionIds[i];
    if (prev === undefined) continue;
    const prevIdx = result.indexOf(prev);
    if (prevIdx !== -1) {
      const next = [...result];
      next.splice(prevIdx + 1, 0, id);
      return next;
    }
  }

  if (result[0] === SELECTOR_COLUMN_ID) {
    const next = [...result];
    next.splice(1, 0, id);
    return next;
  }

  return [id, ...result];
};

/**
 * Merge saved order for one section (static or custom-field) with definition order.
 * Preserves user ordering for columns already in `saved`, and inserts newly visible
 * columns at their definition index instead of appending at the end.
 */
const mergeSectionOrder = (saved: string[], definitionIds: string[]): string[] => {
  const known = new Set(definitionIds);
  const result: string[] = [];
  const placed = new Set<string>();

  for (const id of saved) {
    if (known.has(id) && !placed.has(id)) {
      result.push(id);
      placed.add(id);
    }
  }

  let working = result;
  for (const id of definitionIds) {
    if (placed.has(id)) continue;
    working = insertAtDefinitionPosition(working, id, definitionIds);
    placed.add(id);
  }

  return working;
};

/**
 * Merge saved column order with the current leaf columns so Filler stays last.
 * General columns always precede custom fields (DL-60609): toggling a custom field
 * must not place it before static columns such as Ready for export.
 */
export const normalizeColumnOrder = (
  order: ColumnOrderState,
  allLeafColumnIds: string[]
): ColumnOrderState => {
  const known = new Set(allLeafColumnIds);
  if (!known.has(FILLER_COLUMN_ID)) {
    return order.filter(id => known.has(id));
  }

  const dataColumnIds = getDataColumnIds(allLeafColumnIds);
  const staticDefinition = dataColumnIds.filter(id => !isCustomFieldColumnId(id));
  const customFieldDefinition = dataColumnIds.filter(id => isCustomFieldColumnId(id));

  const saved = order.filter(
    id => id !== SELECTOR_COLUMN_ID && id !== FILLER_COLUMN_ID && known.has(id)
  );
  const savedStatic = saved.filter(id => !isCustomFieldColumnId(id));
  const savedCustomFields = saved.filter(id => isCustomFieldColumnId(id));

  const staticOrder = mergeSectionOrder(savedStatic, staticDefinition);
  const customFieldOrder = mergeSectionOrder(savedCustomFields, customFieldDefinition);

  const result: string[] = [];
  if (known.has(SELECTOR_COLUMN_ID)) {
    result.push(SELECTOR_COLUMN_ID);
  }
  result.push(...staticOrder, ...customFieldOrder, FILLER_COLUMN_ID);
  return result;
};

export const resolveSelectableColumnOrder = (
  order: ColumnOrderState,
  isSelectable: boolean,
  dataColumnIds: string[]
): ColumnOrderState => {
  if (!isSelectable) return order;
  if (order.length === 0) {
    return dataColumnIds.length ? [SELECTOR_COLUMN_ID, ...dataColumnIds] : [SELECTOR_COLUMN_ID];
  }
  if (order[0] === SELECTOR_COLUMN_ID) return order;
  return [SELECTOR_COLUMN_ID, ...order.filter(id => id !== SELECTOR_COLUMN_ID)];
};

export const buildAllLeafColumnIds = (dataColumnIds: string[], isSelectable: boolean): string[] => {
  const base = isSelectable ? [SELECTOR_COLUMN_ID, ...dataColumnIds] : [...dataColumnIds];
  return [...base, FILLER_COLUMN_ID];
};

export const columnOrdersEqual = (a: ColumnOrderState, b: ColumnOrderState): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index]);

export const createFillerColumn = <T>(
  width: number = TABLE_EDGE_SCROLL_GUTTER_WIDTH
): ColumnDef<T> => {
  return {
    id: FILLER_COLUMN_ID,
    accessorKey: 'filler',
    header: '',
    size: width,
    minSize: width,
    maxSize: width,
    enableSorting: false,
    enableResizing: false,
    enableHiding: false,
    meta: { noPadding: true },
    cell: () => null,
  };
};
